const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/DashboardPage/Cards/ECheckinCard/ECheckinCardInfo.tsx';
import moment from 'moment';
import textMap from 'App/pages/DashboardPage/Cards/ECheckinCard/translations';
import { Button, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { css } from '@emotion/css';
import { theme } from 'App/utils/clientSetupManager';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { actionStartECheckin, setSelectedFallNrForECheckin } from 'App/components/eCheckin/actions';
import { AppointmentAndWorkItemData } from './ECheckinCard';
import { goToECheckin } from 'App/utils/history';
import { useFeatureFlags } from 'App/store/featureFlags';
import { Chip } from 'App/components/Chip/Chip';
import { getConfiguration } from 'App/utils/clientSetupGetRessource';
import { useECheckinInitData } from 'App/components/eCheckin/hooks';
import { useCaseData } from 'App/store/case';
import { CaseTypes } from '@cistec/common_web/defs/kisim';
import { DEFAULT_ECHECKIN_WORKITEM_TKEY } from 'App/store/eCheckinItems';

import type { JSX } from "react";

export const DashboardButtonStyle = css`
    text-align: center;
    width: 100%;
`;

const flexAndGap = css`
    position: relative;
    display: flex;
    margin-top: ${theme.spacing(1)};

    ${theme?.breakpoints?.down && theme.breakpoints.down('xs')} {
        flex-direction: column;
    }
`;

const flexColumn = css`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const devFallNr = css`
    position: absolute;
    right: 0;
    bottom: -16px;
`;

const actionBox = css`
    margin-left: auto;
    min-width: 35%;
    align-self: center;
`;

const hiddenAppointmentWrapper = css`
    & .eCheckinFillOutRequest {
        font-weight: 500;
    }

    & .caseInfo {
        color: ${theme.palette.grey[600]};
    }
`;

export interface IProps {
    recentCaseAppointment: AppointmentAndWorkItemData;
}

export const ECheckinCardInfo = (props: IProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const appointment = props.recentCaseAppointment;
    const [fall] = useCaseData(appointment.caseNr || '');
    const history = useHistory();
    const [eCheckinInitData, isECheckinInitDataLoading] = useECheckinInitData(appointment.caseNr, appointment.termNr);

    const configuration = getConfiguration();
    const { data: featureFlags } = useFeatureFlags();
    const eCheckinButtonLabel = formatMessage(appointment.workItemHasEcheckin ? textMap.continueBtn : textMap.beginBtn);

    const termStart = appointment.startDat;
    const mTermStart = moment(termStart);
    const eCheckinFinalizedTime = appointment.checkinFinalizedDat;
    const mECheckinFinalizedTime = moment(eCheckinFinalizedTime);
    const eCheckinFinalizedText =
        eCheckinFinalizedTime &&
        formatMessage(textMap.hiddenAppointmentTextFinalized, {
            date: mECheckinFinalizedTime.format('LL'),
            time: mECheckinFinalizedTime.format('HH:mm'),
        });
    let termStartText: string | null;
    if (appointment.kat === 'VORMERKUNG') {
        termStartText = termStart && mTermStart.format('LL');
    } else if (appointment.type === 'TRIAGE') {
        termStartText = mTermStart.format('LL');
    } else if (appointment.type === 'OPERATION' || appointment.kat === 'VORMERKUNG') {
        termStartText = termStart && mTermStart.format('LL');
    } else {
        termStartText =
            termStart &&
            formatMessage(textMap.termStartText, {
                date: mTermStart.format('LL'),
                time: mTermStart.format('HH:mm'),
            });
    }

    const getCaseTypeLabel = (caseType: CaseTypes) => {
        switch (caseType) {
            case 'S':
                return formatMessage(textMap.stationary);
            case 'T':
                return formatMessage(textMap.stationary);
            case 'A':
                return formatMessage(textMap.ambulant);
        }
    };

    const termTxt = appointment.title ?? '';

    const textForHiddenAppointment = (caseNr: string | undefined): JSX.Element => (
        <div className={hiddenAppointmentWrapper}>
            <div className={'eCheckinFillOutRequest'}>{formatMessage(textMap.hiddenAppointmentText)}</div>
            <div className={'caseInfo'}>
                {caseNr && `${formatMessage(textMap.caseNumber)} ${caseNr}`}
                {` | `}
                {fall?.FALLART && `${formatMessage(textMap.caseType)} ${getCaseTypeLabel(fall.FALLART)}`}
            </div>
        </div>
    );
    const textForHiddenAppointmentFinalized = (caseNr: string | undefined): JSX.Element => (
        <div className={hiddenAppointmentWrapper}>
            <div className={'eCheckinFillOutFinalized'}>{eCheckinFinalizedText}</div>
            <div className={'caseInfo'}>
                {caseNr && `${formatMessage(textMap.caseNumber)} ${caseNr}`}
                {` | `}
                {fall?.FALLART && `${formatMessage(textMap.caseType)} ${getCaseTypeLabel(fall.FALLART)}`}
            </div>
        </div>
    );

    const caseAppointmentDetails = featureFlags.dashboard.appointmentOnCard ? (
        <div className={flexColumn}>
            <Grid key={'terminText'} size={{ xs: 9 }}>
                <Typography variant="h6" component="h2">
                    {termTxt}
                </Typography>
            </Grid>
            <Grid key={'terminStartText'} size={{ xs: 8 }}>
                <Typography>{termStartText}</Typography>
            </Grid>
            {!appointment.isECheckinFinalized ? null : (
                <Typography component="div" sx={{ color: 'text.secondary' }}>
                    {formatMessage(
                        appointment.patientNeedsClearing
                            ? textMap.echeckinWasFinalizedAndGoToCounter
                            : textMap.echeckinWasFinalized
                    )}
                </Typography>
            )}
        </div>
    ) : appointment.isECheckinFinalized ? (
        textForHiddenAppointmentFinalized(appointment.caseNr)
    ) : (
        textForHiddenAppointment(appointment.caseNr)
    );

    const handleClick = () => {
        const tkey = appointment.termNr || DEFAULT_ECHECKIN_WORKITEM_TKEY;
        dispatch(setSelectedFallNrForECheckin(appointment.caseNr, tkey));

        if (configuration.eCheckin.form) {
            if (eCheckinInitData) {
                dispatch<any>(actionStartECheckin(eCheckinInitData, appointment.caseNr, tkey));
            }
        } else {
            goToECheckin(history);
        }
    };

    return (
        <Grid className={flexAndGap}>
            {process.env.NODE_ENV === 'development' && (
                <Chip color={'black'} className={devFallNr}>
                    FallNr: {appointment.caseNr} | WorkItem-TermNr: {appointment.termNr}
                </Chip>
            )}
            {caseAppointmentDetails}
            {!appointment.isECheckinFinalized && (
                <Grid size={{ xs: 3 }} className={actionBox}>
                    <Button
                        color="primary"
                        component="h2"
                        variant="contained"
                        size={'medium'}
                        onClick={handleClick}
                        className={DashboardButtonStyle}
                    >
                        {eCheckinButtonLabel}
                    </Button>
                </Grid>
            )}
        </Grid>
    );
};
